import { COMPANY_DRAWERS, COMPANY_PROFILE_CONFIG } from 'features/inventory-profiles/constants';
import { buildLastPaymentDetail, formatLocation } from 'features/inventory-profiles/utils';
import strings from 'strings';

const COPIES = strings.inventoryProfiles;

/**
 * The only code that knows the Company API shape. Pure: no rendering, no hooks.
 * @param {Object} company Company profile
 * @returns {{ header: Object, summary: Object, status: Object }}
 */
export const formatCompanyResponse = (company = {}) => ({
  header: {
    entityType: COMPANY_PROFILE_CONFIG.type,
    title: company.name ?? '',
    lastActivity: company.last_activity_date ?? null
  },
  summary: {
    company: null,
    location: formatLocation(company.address?.city),
    created: {
      date: company.created_at,
      by: company.createdBy?.personalInformation?.full_name
    },
    details: [buildLastPaymentDetail(company.last_payment)]
  },
  status: {
    inventoryType: company.type,
    entityTypeId: COMPANY_PROFILE_CONFIG.id
  }
});

/**
 * Company type dialog: mandatory when the recruiter must confirm the type, optional from the menu.
 * @param {Object} params
 * @param {Object} [params.companyTypeReassure] `company.companyTypeReassure`
 * @param {string | null} params.drawer From useProfileDrawer
 * @param {() => void} params.closeDrawer From useProfileDrawer
 * @returns {{ isOpen: boolean, reassure: Object | undefined, dialogProps: Object }}
 */
export const getCompanyTypeDialog = ({ companyTypeReassure, drawer, closeDrawer }) => {
  const shouldAskRecruiter = Boolean(companyTypeReassure?.shouldAskRecruiter);

  return {
    isOpen: shouldAskRecruiter || drawer === COMPANY_DRAWERS.CompanyType,
    reassure: companyTypeReassure?.inProgress,
    dialogProps: shouldAskRecruiter
      ? {}
      : { showCloseButton: true, mustComplete: false, isOpened: true, onClose: closeDrawer }
  };
};

/**
 * Company ⋮ menu, in V1 order: edit, recruiters, own items, claim. Company has no restrictions.
 * Pure: it receives booleans, lists of items and handlers, only the ones it uses, so it reads
 * no hook, no user and no feature flag.
 * @param {Object} params
 * @param {Object} params.permissions Result of useCompanyPermissions
 * @param {boolean} params.canManage From useProfileAccountability
 * @param {boolean} params.canView From useProfileAccountability
 * @param {Array} params.searchProjectMenuItems `SearchProjectsMenuItems` of useSearchProjectInProfiles (V1)
 * @param {(name: string) => () => void} params.openDrawer From useProfileDrawer
 * @returns {Array<{ title: string, action: () => void, visible: boolean }>}
 */
export const buildCompanyMenuItems = ({
  canManage,
  canView,
  openDrawer,
  permissions,
  searchProjectMenuItems
}) => [
  {
    title: COPIES.common.editProfile,
    action: openDrawer(COMPANY_DRAWERS.Edit),
    visible: permissions.canEdit
  },
  {
    title: canManage ? COPIES.menu.manageRecruiters : COPIES.menu.viewRecruiters,
    action: openDrawer(COMPANY_DRAWERS.Recruiters),
    visible: canManage || canView
  },
  ...searchProjectMenuItems,
  {
    title: COPIES.sections.companies.updateType,
    action: openDrawer(COMPANY_DRAWERS.CompanyType),
    visible: permissions.canUpdateCompanyType
  },
  {
    title: COPIES.menu.addAction,
    action: openDrawer(COMPANY_DRAWERS.Task),
    visible: permissions.canUseDailyPlan
  },
  {
    title: COPIES.accountability.action,
    action: openDrawer(COMPANY_DRAWERS.Claim),
    visible: permissions.canClaimAccountability
  }
];
